import React, { useEffect, useRef, useState } from "react";
import styles from "./ServiceOptionSettings.module.css";

import { useAuth } from "../../Context/AuthContext";
import {
  fetchServiceOptions,
  createServiceOption,
  updateServiceOption,
  deleteServiceOption,
} from "../../Api/Backend/ServiceOption";

// Backend PublishingMethodScope enum values + display labels
const SCOPE_OPTIONS = [
  { value: "JABI", label: "자비 (JABI)" },
  { value: "AMAZON", label: "아마존 (AMAZON)" },
];

const byDisplayOrder = (a, b) => {
  const av = a.displayOrder === "" || a.displayOrder == null ? Number.MAX_SAFE_INTEGER : a.displayOrder;
  const bv = b.displayOrder === "" || b.displayOrder == null ? Number.MAX_SAFE_INTEGER : b.displayOrder;
  return av - bv;
};

const toRow = (dto) => ({
  serviceOptionId: dto.serviceOptionId ?? null,
  category: dto.category ?? "",
  title: dto.title ?? "",
  description: dto.description ?? "",
  unitPrice: dto.unitPrice ?? 0,
  unit: dto.unit ?? "",
  isRequired: dto.isRequired ?? false,
  publishingMethodScope: dto.publishingMethodScope ?? "JABI",
  sectionHeader: dto.sectionHeader ?? "",
  displayOrder: dto.displayOrder ?? "",
  extraInfo: dto.extraInfo ?? "",
  dirty: false, // unsaved-changes flag (local UI only)
});

const buildPayload = (item) => ({
  category: item.category,
  title: item.title,
  description: item.description,
  unitPrice: Number(item.unitPrice) || 0,
  unit: item.unit,
  isRequired: !!item.isRequired,
  publishingMethodScope: item.publishingMethodScope,
  sectionHeader: item.sectionHeader,
  displayOrder:
    item.displayOrder === "" || item.displayOrder == null
      ? null
      : Number(item.displayOrder),
  extraInfo: item.extraInfo,
});

const rowKey = (s) => (s.serviceOptionId != null ? s.serviceOptionId : s._tempId);

const move = (arr, from, to) => {
  const copy = arr.slice();
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
};

let tempIdSeq = -1; // negative ids for unsaved rows (never sent to backend)

export default function ServiceOptionSettings() {
  const auth = useAuth();

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [activeScope, setActiveScope] = useState("JABI");

  const dragIndexRef = useRef(null);

  useEffect(() => {
    fetchServiceOptions({
      onSuccess: (data) => {
        const rows = (Array.isArray(data) ? data : []).map(toRow).sort(byDisplayOrder);
        setServices(rows);
        setLoading(false);
      },
      onError: (error) => {
        console.error("Error fetching service options:", error);
        alert("서비스 옵션을 불러올 수 없습니다.");
        setLoading(false);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (item) => {
    if (!item.category?.trim() || !item.title?.trim()) {
      alert("분류(Category)와 항목명(Title)은 필수입니다.");
      return;
    }

    setSavingId(rowKey(item));
    try {
      if (item.serviceOptionId != null) {
        const updated = await updateServiceOption({
          auth,
          serviceOptionId: item.serviceOptionId,
          payload: buildPayload(item),
        });
        applySaved(item, updated);
      } else {
        const created = await createServiceOption({ auth, payload: buildPayload(item) });
        applySaved(item, created);
      }
      alert("저장되었습니다.");
    } catch (error) {
      console.error("Error saving service option:", error);
      alert(error?.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setSavingId(null);
    }
  };

  const applySaved = (item, savedDto) => {
    if (!savedDto) return;
    const saved = toRow(savedDto);
    setServices((prev) =>
      prev
        .map((s) => (rowKey(s) === rowKey(item) ? { ...saved, _tempId: undefined } : s))
        .sort(byDisplayOrder)
    );
  };

  const handleDelete = async (item) => {
    if (!window.confirm("이 항목을 삭제하시겠습니까?")) return;

    if (item.serviceOptionId == null) {
      setServices((prev) => prev.filter((s) => rowKey(s) !== rowKey(item)));
      return;
    }

    setSavingId(rowKey(item));
    try {
      await deleteServiceOption({ auth, serviceOptionId: item.serviceOptionId });
      setServices((prev) => prev.filter((s) => rowKey(s) !== rowKey(item)));
    } catch (error) {
      console.error("Error deleting service option:", error);
      alert(error?.message || "삭제 중 오류가 발생했습니다.");
    } finally {
      setSavingId(null);
    }
  };

  const handleInputChange = (key, field, value) => {
    setServices((prev) =>
      prev.map((s) =>
        rowKey(s) === key
          ? { ...s, [field]: field === "isRequired" ? !s.isRequired : value, dirty: true }
          : s
      )
    );
  };

  const handleAddItem = () => {
    setServices((prev) => [
      ...prev,
      {
        serviceOptionId: null,
        _tempId: tempIdSeq--,
        category: "",
        title: "",
        description: "",
        unitPrice: 0,
        unit: "",
        isRequired: false,
        publishingMethodScope: activeScope, // land in the active tab
        sectionHeader: "",
        displayOrder: "",
        extraInfo: "",
        dirty: true,
      },
    ]);
  };

  // Persist existing rows whose displayOrder changed after a reorder.
  const persistOrderChanges = async (oldList, newList) => {
    const oldByKey = new Map(oldList.map((s) => [rowKey(s), s]));
    const changed = newList.filter(
      (s) =>
        s.serviceOptionId != null &&
        oldByKey.get(rowKey(s))?.displayOrder !== s.displayOrder
    );
    if (changed.length === 0) return;

    setReordering(true);
    try {
      await Promise.all(
        changed.map((s) =>
          updateServiceOption({
            auth,
            serviceOptionId: s.serviceOptionId,
            payload: buildPayload(s),
          })
        )
      );
      // Persisted rows are now clean (drag sends the full payload).
      const changedKeys = new Set(changed.map((s) => rowKey(s)));
      setServices((prev) =>
        prev.map((s) => (changedKeys.has(rowKey(s)) ? { ...s, dirty: false } : s))
      );
    } catch (error) {
      console.error("Error saving new order:", error);
      alert(error?.message || "순서 저장 중 오류가 발생했습니다.");
    } finally {
      setReordering(false);
    }
  };

  // Save every row with unsaved changes at once.
  const handleSaveAll = async () => {
    const dirtyRows = services.filter((s) => s.dirty);
    if (dirtyRows.length === 0) return;

    const invalid = dirtyRows.filter(
      (r) => !r.category?.trim() || !r.title?.trim()
    );
    if (invalid.length > 0) {
      alert("분류(Category)와 항목명(Title)이 비어있는 행이 있습니다. 먼저 채워주세요.");
      return;
    }

    setSavingAll(true);
    try {
      const results = await Promise.all(
        dirtyRows.map(async (r) => {
          const saved =
            r.serviceOptionId != null
              ? await updateServiceOption({
                  auth,
                  serviceOptionId: r.serviceOptionId,
                  payload: buildPayload(r),
                })
              : await createServiceOption({ auth, payload: buildPayload(r) });
          return { key: rowKey(r), saved };
        })
      );

      setServices((prev) =>
        prev
          .map((s) => {
            const res = results.find((r) => r.key === rowKey(s));
            return res?.saved ? { ...toRow(res.saved), _tempId: undefined } : s;
          })
          .sort(byDisplayOrder)
      );
      alert("모든 변경사항이 저장되었습니다.");
    } catch (error) {
      console.error("Error saving all:", error);
      alert(error?.message || "일괄 저장 중 오류가 발생했습니다.");
    } finally {
      setSavingAll(false);
    }
  };

  const handleDragStart = (index) => (e) => {
    dragIndexRef.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (key) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverKey !== key) setDragOverKey(key);
  };

  const handleDrop = (index) => (e) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    dragIndexRef.current = null;
    setDragOverKey(null);
    if (from == null || from === index) return;

    // Reorder within the active scope only; renumber that tab's displayOrder.
    const visible = services
      .filter((s) => (s.publishingMethodScope || "JABI") === activeScope)
      .slice()
      .sort(byDisplayOrder);
    const reordered = move(visible, from, index).map((s, i) => ({
      ...s,
      displayOrder: (i + 1) * 10,
    }));
    const byKey = new Map(reordered.map((s) => [rowKey(s), s]));
    setServices((prev) => prev.map((s) => byKey.get(rowKey(s)) ?? s));
    persistOrderChanges(visible, reordered);
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setDragOverKey(null);
  };

  const dirtyCount = services.filter((s) => s.dirty).length;
  const visibleServices = services
    .filter((s) => (s.publishingMethodScope || "JABI") === activeScope)
    .slice()
    .sort(byDisplayOrder);

  return (
    <div className={styles.container}>
      <h1 className={styles.header}>모의견적서 양식 설정</h1>
      <p className={styles.description}>
        좌측핸들을 드래그하여 순서를 조정할수 있습니다.
        {reordering && " (순서 저장 중...)"}
      </p>

      <div className={styles.toolbar}>
        <div className={styles.methodTabs}>
          {SCOPE_OPTIONS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={`${styles.methodTab}${activeScope === tab.value ? " " + styles.active : ""}`}
              onClick={() => setActiveScope(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {dirtyCount >= 2 && (
          <div className={styles.saveAllBar}>
            <span className={styles.saveAllCount}>저장되지 않은 변경 {dirtyCount}건</span>
            <button
              className={styles.saveAllButton}
              onClick={handleSaveAll}
              disabled={savingAll}
            >
              {savingAll ? "저장 중..." : "Save All"}
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <p className={styles.description}>불러오는 중...</p>
      ) : (
        <>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th aria-label="순서 변경" />
                  <th>Header</th>
                  <th>Category</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Unit Price (원)</th>
                  <th>Unit</th>
                  <th>Scope</th>
                  <th>Order</th>
                  <th>Extra Info</th>
                  <th>Required</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleServices.map((service, index) => {
                  const key = rowKey(service);
                  return (
                    <tr
                      key={key}
                      onDragOver={handleDragOver(key)}
                      onDrop={handleDrop(index)}
                      className={dragOverKey === key ? styles.dragOverRow : undefined}
                    >
                      <td>
                        <span
                          className={styles.dragHandle}
                          draggable
                          onDragStart={handleDragStart(index)}
                          onDragEnd={handleDragEnd}
                          title="드래그하여 순서 변경"
                        >
                          ⠿
                        </span>
                      </td>
                      <td>
                        <input
                          type="text"
                          value={service.sectionHeader}
                          onChange={(e) =>
                            handleInputChange(key, "sectionHeader", e.target.value)
                          }
                          className={styles.input}
                          placeholder="블록 제목 (파란색, 선택)"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={service.category}
                          onChange={(e) =>
                            handleInputChange(key, "category", e.target.value)
                          }
                          className={styles.input}
                          placeholder="분류 (소제목)"
                        />
                      </td>
                      <td>
                        <textarea
                          value={service.title}
                          onChange={(e) =>
                            handleInputChange(key, "title", e.target.value)
                          }
                          className={styles.titleArea}
                          rows={2}
                          placeholder="줄바꿈은 Enter"
                        />
                      </td>
                      <td>
                        <textarea
                          value={service.description}
                          onChange={(e) =>
                            handleInputChange(key, "description", e.target.value)
                          }
                          className={styles.textarea}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={service.unitPrice}
                          onChange={(e) =>
                            handleInputChange(key, "unitPrice", e.target.value)
                          }
                          className={styles.input}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={service.unit}
                          onChange={(e) =>
                            handleInputChange(key, "unit", e.target.value)
                          }
                          className={styles.input}
                        />
                      </td>
                      <td>
                        <select
                          value={service.publishingMethodScope}
                          onChange={(e) =>
                            handleInputChange(
                              key,
                              "publishingMethodScope",
                              e.target.value
                            )
                          }
                          className={styles.input}
                        >
                          {SCOPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          value={service.displayOrder}
                          onChange={(e) =>
                            handleInputChange(key, "displayOrder", e.target.value)
                          }
                          className={styles.input}
                        />
                      </td>
                      <td>
                        <textarea
                          value={service.extraInfo}
                          onChange={(e) =>
                            handleInputChange(key, "extraInfo", e.target.value)
                          }
                          className={styles.textarea}
                        />
                      </td>
                      <td className={styles.checkboxCell}>
                        <input
                          type="checkbox"
                          checked={service.isRequired}
                          onChange={() => handleInputChange(key, "isRequired")}
                        />
                      </td>
                      <td>
                        <button
                          onClick={() => handleSave(service)}
                          className={styles.saveButton}
                          disabled={savingId === key}
                        >
                          {savingId === key ? "저장 중..." : "Save"}
                        </button>
                        <button
                          onClick={() => handleDelete(service)}
                          className={styles.deleteButton}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button onClick={handleAddItem} className={styles.addButton}>
            Add New Service Option
          </button>
        </>
      )}
    </div>
  );
}
